// =============================================================================
// PEDIGREE PRINT SERVICE
// =============================================================================

import helper_dom_printing from '../../../helpers/helper-dom-pdf';
import familyApi from "../../../api/family-api";

import {
  ApiNodeTypes,
  ViewStatusOptions,
} from '../pedigree-constants';

const ANCESTRY_FONT_OFFSET = 15;

export class PedigreePrintService {
  constructor(pedigreeWrapper) {
      this.pedigreeWrapper = pedigreeWrapper
  }

  // function to get the payload and then send to print endpoint to get the pdf/image data
  async getprintPayload(fileType, singlePage, orientation) {
    // set useOnlyRenderVisibleElements to false to render the entire pedigree first before getting the svg string
    this.pedigreeWrapper.setState({ useOnlyRenderVisibleElements: false }, async() => {
      let payload = {}
      try {
        switch (fileType) {
          case 'pdf':
            payload = this.preparePDF(singlePage, orientation);
            await this.printPedigree(payload);
            break;
          case 'image':
            const { pedigireeString, imageHeight, imageWidth } = this.preparePedigreeImage();
            payload = { svgData: pedigireeString, height: imageHeight, width: imageWidth };
            await this.handleSaveImageClick(payload);
            break;
        }
        // this.sendPrintDataToPedigreeViewerParent(payload);
      } catch (err) {
        console.log(err)
        this.pedigreeWrapper.setState({ openPrintingErrorModal: true })
      }
    });
  }

  // function to prepare payload (svg) for pdf
  preparePDF(singlePage, orientation = 'landscape') {
    let minHeight = 750
    let minWidth = 1200
    const subtextPaddingX = 50
    const subtextPaddingWidth = 75
    const printScale = singlePage ? 'FitToPage' : 'FitToOnePageTall'

    // if single page is true we set the scale to fit on to one page else we set the scale to 1
    // create header in the top right corner to indicate which patient the pdf is for
    let nodes = this.pedigreeWrapper.reactFlowInstance.getNodes();
    let nodesBounds = this.pedigreeWrapper.pedigreeCallbacks.getNodesBounds(nodes);

    let farLeftSubtext = nodes.find(n =>
      n.nodeType === ApiNodeTypes.PERSON &&
      n.data?.subtext &&
      n.position?.x === nodesBounds.x
    );

    if (farLeftSubtext) {
      nodesBounds['width'] += subtextPaddingWidth
      nodesBounds['x'] -= subtextPaddingX
    }

    const proband = this.pedigreeWrapper.props.getPedigreeData().getProband();

    const cookies = this.pedigreeWrapper.getCookies();
    let is_identified = cookies.viewStatus === ViewStatusOptions.IDENTIFIED;

    let patientData = {
      firstName: proband.first_name,
      lastName: proband.last_name,
      id: proband.patient_id,
      dob: proband.dob,
      age: proband.age_string ? proband.age_string : proband.age,
      is_identified
    }

    let headerSVGString = helper_dom_printing.createPatientHeader(patientData, orientation)
    let footerSvg = helper_dom_printing.createPatientFooter(orientation)

    //create svg string from dom and pass to api end pt to generate pdf
    let pedigree_svg = document.getElementById('react-flow-pedigree')

    // determine scale based on zoom factor of pedigree and determine non scaled height and width of pedigree

    let printHeight = nodesBounds['height'] + ANCESTRY_FONT_OFFSET
    let printWidth = nodesBounds['width']
    let xOffset = nodesBounds['x']
    let yOffset = nodesBounds['y'] - ANCESTRY_FONT_OFFSET
    let svgString = helper_dom_printing.createSVGPdf(pedigree_svg, printWidth, printHeight, xOffset, yOffset)

    if (printHeight < minHeight) printHeight = minHeight
    if (printWidth < minWidth) printWidth = minWidth

    let printPayload = {
      height: printHeight,
      width: printWidth,
      scale: printScale,
      svgData: svgString,
      header: headerSVGString,
      footer: footerSvg['footerSVG'],
      footerHeight: footerSvg['footerHeight'],
      orientation
    }
    return printPayload
  }

  // function to prepare payload (svg) for image
  preparePedigreeImage() {
    const proband = this.pedigreeWrapper.props.getPedigreeData().getProband()

    const cookies = this.pedigreeWrapper.getCookies();
    let is_identified = cookies.viewStatus === ViewStatusOptions.IDENTIFIED;


    let patientData = {
      firstName: proband.first_name,
      lastName: proband.last_name,
      id: proband.patient_id,
      dob: proband.dob,
      age: proband.age_string ? proband.age_string : proband.age ? proband.age : '',
      is_identified
    }

    let nodes = this.pedigreeWrapper.reactFlowInstance.getNodes();
    let nodesBounds = this.pedigreeWrapper.pedigreeCallbacks.getNodesBounds(nodes);
    let pedigree_svg = document.getElementById('react-flow-pedigree')

    let printHeight = nodesBounds['height'] + ANCESTRY_FONT_OFFSET
    let printWidth = nodesBounds['width']
    let xOffset = nodesBounds['x']
    let yOffset = nodesBounds['y'] - ANCESTRY_FONT_OFFSET
    const {
      pedigireeString,
      imageHeight,
      imageWidth
    } = helper_dom_printing.createImageString(pedigree_svg, printWidth, printHeight, patientData, xOffset, yOffset)

    return { pedigireeString, imageHeight, imageWidth }
  }

  // function to create pdf blob from base64 data that we get from the print pdf endpoint
  async printPedigree(printPayload, isMobilePrint=false) {
    let data = null
    try {
      data = await this.pedigreeWrapper.printAPICall(printPayload, isMobilePrint)
    } catch (err) {
      this.pedigreeWrapper.setState({ openPrintingErrorModal: true })
    }

    const blob = this.pedigreeWrapper.createBlob(data, 'application/pdf')
    const blobUrl = URL.createObjectURL(blob)

    if (blob.size < 50) {
      this.pedigreeWrapper.setState({ openPrintingErrorModal: true })
      return;
    }

    this.pedigreeWrapper.setState({ imageSrc: blobUrl, blobType: 'pdf', base64Data: data });
  }

  // function to create image blob from base64 data that we get from the print image endpoint
  async handleSaveImageClick(printPayload) {
    let data = {}
    try {
      data = await familyApi.flux_image_generator(printPayload)
    } catch (err) {
      console.log('testing image print error', err)
      this.pedigreeWrapper.setState({ openPrintingErrorModal: true })
      return;
    }

    const blob = this.pedigreeWrapper.createBlob(data, 'image/png')
    const blobUrl = URL.createObjectURL(blob)

    if (blob.size < 50) {
      console.log(blob.size)
      console.log('testing image print error')

      this.pedigreeWrapper.setState({ openPrintingErrorModal: true })
      return;
    }

    this.pedigreeWrapper.reRenderLegend();

    this.pedigreeWrapper.setState({ imageSrc: blobUrl, blobType: 'image', base64Data: data })
  }

  // Handle print click with blob URL, function to download the file or send message to pedigree viewer or patient app parent window
  async handlePrintClick(blobUrl, isMobilePrint = false, data = {}, base64 = false) {
		const file = this.pedigreeWrapper.state.blobType || 'pdf'
		let file_name = this.pedigreeWrapper.getFileName(file);
    if (isMobilePrint) {
       if (window.parent && window.parent !== window && typeof window.parent.postMessage === 'function') {
        try {
          window.parent.postMessage(
            JSON.stringify({ data, type: 'print-data', file_name }),
            "*"
          );
          return;
        } catch (error) {
          console.error('Failed to post message to parent:', error);
        }
      }
    }

		let base64Blob = null;

    if (this.pedigreeWrapper.props.imported_pedigree_json_data){
      if (base64) {
        const base64JsonData = {
          base64String: this.pedigreeWrapper.state.base64Data,
        };

        const jsonString = JSON.stringify(base64JsonData, null, 2);

        base64Blob = new Blob([jsonString], { type: 'application/json' });

        let proband = this.pedigreeWrapper.props.getPedigreeData().getProband()
        file_name = `${proband.last_name ? proband.last_name + '-' : ''}${proband.first_name ? proband.first_name + '-' : ''}${proband.patient_id ? proband.patient_id : ''}`
        if ((proband.last_name || proband.first_name) && !proband.patient_id && file_name) {
          file_name = file_name.slice(0, -1);
        }
      }
    }

    if (!base64){
      const downloadLink = document.createElement("a");
      downloadLink.href = this.pedigreeWrapper.props.imported_pedigree_json_data && base64 ? URL.createObjectURL(base64Blob) : blobUrl;
      downloadLink.download = file_name;
      document.body.appendChild(downloadLink);
      downloadLink.click();
    }
    else{
      this.pedigreeWrapper.sendPrintDataToPedigreeViewerParent(this.pedigreeWrapper.state.base64Data);
    }

    // log print event for pedigree viewer
		await PedigreePrintService.logPedigreeViewerPrint(this.pedigreeWrapper.props.imported_pedigree_json_data);
  }

  static async logPedigreeViewerPrint(imported_pedigree_json_data) {
		// Track the print pedigree event
		if (imported_pedigree_json_data) {
			let organization_identifier = sessionStorage.getItem("organization_identifier");
			await familyApi.flux_pedigree_viewer_print_log({
				organization_identifier: organization_identifier,
			});
		}
	}

}